import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import './AdminHeroes.css';

function AdminHeroes() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchPendingSuggestions();
  }, []);

  const fetchPendingSuggestions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `/api/famous-people/admin/suggestions/pending`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSuggestions(response.data);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (suggestionId) => {
    if (!window.confirm('Are you sure you want to approve this suggestion?')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `/api/famous-people/admin/suggestions/${suggestionId}/approve`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      alert('Suggestion approved and published!');
      fetchPendingSuggestions();
      setSelectedSuggestion(null);
    } catch (error) {
      console.error('Error approving suggestion:', error);
      alert(error.response?.data?.error || 'Failed to approve suggestion');
    }
  };

  const handleReject = async (suggestionId) => {
    if (!rejectNotes.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `/api/famous-people/admin/suggestions/${suggestionId}/reject`,
        { admin_notes: rejectNotes },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      alert('Suggestion rejected');
      fetchPendingSuggestions();
      setSelectedSuggestion(null);
      setRejectNotes('');
    } catch (error) {
      console.error('Error rejecting suggestion:', error);
      alert(error.response?.data?.error || 'Failed to reject suggestion');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      politics: '#3b82f6',
      arts: '#8b5cf6',
      science: '#10b981',
      sports: '#f59e0b',
      business: '#ef4444',
      social_reform: '#ec4899',
      literature: '#6366f1',
      education: '#14b8a6'
    };
    return colors[category] || '#6b7280';
  };

  const formatYears = (birthYear, deathYear) => {
    if (!birthYear) return '';
    if (deathYear) return `${birthYear} - ${deathYear}`;
    return `${birthYear} - Present`;
  };

  return (
    <div>
      <Navbar />

      <div className="admin-heroes-page">
        <div className="admin-header">
          <h1>Review Hero Suggestions</h1>
          <p>Pending suggestions: {suggestions.length}</p>
        </div>

        {loading ? (
          <div className="loading">Loading suggestions...</div>
        ) : suggestions.length === 0 ? (
          <div className="no-suggestions">
            <p>✅ No pending suggestions to review!</p>
          </div>
        ) : (
          <div className="suggestions-list">
            {suggestions.map(suggestion => (
              <div key={suggestion.suggestion_id} className="suggestion-card">
                <div className="suggestion-header">
                  <div>
                    <h3>{suggestion.name}</h3>
                    {suggestion.title && <p className="suggestion-title">{suggestion.title}</p>}
                    <p className="suggestion-years">{formatYears(suggestion.birth_year, suggestion.death_year)}</p>
                    <span
                      className="category-badge"
                      style={{ backgroundColor: getCategoryColor(suggestion.category) }}
                    >
                      {suggestion.category.replace('_', ' ')}
                    </span>
                  </div>
                  {suggestion.photo_url && (
                    <img src={suggestion.photo_url} alt={suggestion.name} className="suggestion-photo" />
                  )}
                </div>

                <div className="suggestion-meta">
                  <p><strong>Suggested by:</strong> {suggestion.suggested_by_name || 'Unknown'}</p>
                  <p><strong>Submitted:</strong> {new Date(suggestion.created_at).toLocaleDateString()}</p>
                  <p><strong>Identity ID:</strong> {suggestion.caste_id}</p>
                </div>

                <div className="suggestion-content">
                  <h4>Biography:</h4>
                  <p>{suggestion.biography}</p>

                  {suggestion.achievements && suggestion.achievements.length > 0 && (
                    <>
                      <h4>Achievements:</h4>
                      <ul>
                        {suggestion.achievements.map((achievement, index) => (
                          <li key={index}>{achievement}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {suggestion.notable_quote && (
                    <>
                      <h4>Notable Quote:</h4>
                      <blockquote>"{suggestion.notable_quote}"</blockquote>
                    </>
                  )}
                </div>

                <div className="suggestion-actions">
                  <button
                    className="approve-btn"
                    onClick={() => handleApprove(suggestion.suggestion_id)}
                  >
                    ✅ Approve & Publish
                  </button>
                  <button
                    className="reject-btn"
                    onClick={() => setSelectedSuggestion(suggestion.suggestion_id)}
                  >
                    ❌ Reject
                  </button>
                </div>

                {selectedSuggestion === suggestion.suggestion_id && (
                  <div className="reject-form">
                    <textarea
                      placeholder="Reason for rejection (will be shown to the user)..."
                      value={rejectNotes}
                      onChange={(e) => setRejectNotes(e.target.value)}
                      rows="3"
                    />
                    <div className="reject-actions">
                      <button onClick={() => {
                        setSelectedSuggestion(null);
                        setRejectNotes('');
                      }}>
                        Cancel
                      </button>
                      <button
                        className="confirm-reject-btn"
                        onClick={() => handleReject(suggestion.suggestion_id)}
                      >
                        Confirm Rejection
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminHeroes;

